import React, { useContext } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { RouteContext, FlagsContext } from '../../context';
import {
  BILLING_PAGE,
  CUSTOMIZATION_PAGE,
  DOMAIN_PAGE,
  LANDING_PAGE_SETTING_PAGE,
  MAIN_SETTING_PAGE,
  MEMBERS_PAGE,
  API_SETTINGS_PAGE,
  MESSAGING_PAGE,
  PAYMENTS_PAGE,
  PLAN_PAGE,
  REFERRAL_PAGE,
  PROFILE_PAGE,
  APPS_PAGE,
  FILES_SETTINGS_PAGE,
  FILES_PAGE,
  SETTINGS_PAGE_ROOT_PATH,
  EXTENSION_ITEM,
  PORTAL_ITEM,
  NOTIFICATIONS_SETTINGS_PAGE,
  SIGNATURE_PAGE_V2,
  MODULE_MANAGEMENT_PAGE,
} from '../../constants';
import { useRouteAccess } from '../../routes/routeAccess';
import { RootState } from '../../store';
import * as Constants from '../../constants';
import { useNotificationsBadge } from './NotificationBadges';
import { IconNameToComponent, IconType } from '../Icons';
import { NavItem, NavSection } from './SidebarNav';
import { UpgradeBadge } from './UpgradeBadge';
import { getModuleIcon, getModuleName } from '../../utils/ModuleSettingsUtils';
import { usePlanStatus } from '../../hooks/usePlanStatusHook';
import { ExtensionTooltipTitle } from '../Tooltip';
import { useGetPortalConfigQuery } from '../../services/api';

const extensionNavItemIdPrefix = 'sidebar-extension-';
const moduleNavItemIdPrefix = 'sidebar-';

export enum CoreModuleSortOrder {
  MESSAGES = 0,
  FILES = 1,
  FORMS = 2,
  PAYMENTS = 3,
  KNOWLEDGE_BASE = 4,
  EXTENSIONS = 5,
}

interface QueryParams {
  id: string;
}

enum ExtensionItemViewLocation {
  IN_PORTAL = 'in-portal',
  NEW_TAB = 'new-tab',
}

export type SidebarType = 'main' | 'settings';
export const useSidebarItems = (sidebarType?: SidebarType) => {
  const routeAccess = useRouteAccess();
  const { pathname, query } = useContext(RouteContext);
  const {
    EnableNotificationSettings,
    EnableImprovedReferralsProgram,
    EnableFilesNotificationCenter,
  } = useContext(FlagsContext);
  const planStatus = usePlanStatus();

  const { id: queryIdParam } = query as QueryParams;
  const {
    isClient,
    extensionsSettings,
    extensionItems,
    moduleSettings,
  } = useSelector(
    (state: RootState) => ({
      extensionsSettings: state.settings.extensionsSettings,
      moduleSettings: state.settings.moduleSettings,
      extensionItems: state.dashboard.items,
      isClient: state.user.isClient,
    }),
    shallowEqual,
  );
  const { data: portalConfigurations } = useGetPortalConfigQuery();
  const shouldFilterNonClientItems =
    isClient && portalConfigurations?.structFields?.disableCompanies;

  const setupExtensionConfigSet = extensionItems
    .filter(extensionItem => extensionItem.fields.status === 'connected')
    .reduce(
      (extensionConfigSet: Record<string, boolean>, extensionItem) => ({
        ...extensionConfigSet,
        [extensionItem.fields.extensionConfigId || 'default']: true,
      }),
      {},
    );

  const meSectionNavItems: Array<NavItem> = [
    {
      htmlId: 'account-setting-page',
      label: PROFILE_PAGE.label,
      path: PROFILE_PAGE.path,
      icon: PROFILE_PAGE.icon,
      canAccess: true,
    },
  ];

  const workspaceSectionNavItems: Array<NavItem> = [
    {
      htmlId: 'main-setting-page',
      label: MAIN_SETTING_PAGE.label,
      path: MAIN_SETTING_PAGE.path,
      icon: MAIN_SETTING_PAGE.icon,
      canAccess: routeAccess.allowSettingsMainPage,
      selected: pathname === SETTINGS_PAGE_ROOT_PATH,
    },
    {
      htmlId: 'domain-setting-page',
      label: DOMAIN_PAGE.label,
      path: DOMAIN_PAGE.path,
      icon: DOMAIN_PAGE.icon,
      canAccess: routeAccess.allowSettingsDomainPage,
      children: (
        <UpgradeBadge
          show={planStatus.isStarterPlan || planStatus.requireProUpgrade}
        />
      ),
    },
    {
      htmlId: 'members-setting-page',
      label: MEMBERS_PAGE.label,
      path: MEMBERS_PAGE.path,
      icon: MEMBERS_PAGE.icon,
      canAccess: routeAccess.allowSettingsMembersPage,
    },
    ...(EnableNotificationSettings
      ? [
          {
            htmlId: 'notification-setting-page',
            label: NOTIFICATIONS_SETTINGS_PAGE.label,
            path: NOTIFICATIONS_SETTINGS_PAGE.path,
            icon: NOTIFICATIONS_SETTINGS_PAGE.icon,
            canAccess: routeAccess.allowNotificationSettings,
          },
        ]
      : []),
    {
      htmlId: 'api-settings-page',
      label: API_SETTINGS_PAGE.label,
      path: API_SETTINGS_PAGE.path,
      icon: API_SETTINGS_PAGE.icon,
      canAccess: routeAccess.allowSettingsApiPage,
      children: (
        <UpgradeBadge
          show={planStatus.isStarterPlan || planStatus.isTrialing}
        />
      ),
    },
    {
      htmlId: 'app-setting-page',
      label: APPS_PAGE.label,
      path: APPS_PAGE.path,
      icon: APPS_PAGE.icon,
      canAccess: routeAccess.allowSettingsAppsPage,
    },
    {
      htmlId: 'billing-setting-page',
      label: BILLING_PAGE.label,
      path: BILLING_PAGE.path,
      icon: BILLING_PAGE.icon,
      canAccess: routeAccess.allowSettingsBillingPage,
    },

    {
      htmlId: 'plan-setting-page',
      label: PLAN_PAGE.label,
      path: PLAN_PAGE.path,
      icon: PLAN_PAGE.icon,
      canAccess: routeAccess.allowSettingsPlanPage,
    },
    ...(EnableImprovedReferralsProgram
      ? []
      : [
          {
            htmlId: 'referral-setting-page',
            label: REFERRAL_PAGE.label,
            path: REFERRAL_PAGE.path,
            icon: REFERRAL_PAGE.icon,
            canAccess: routeAccess.allowSettingsReferralPage,
          },
        ]),
  ];

  const inAccessibleNavItemsForClient: Array<NavItem['label']> = [
    MAIN_SETTING_PAGE.label,
    MEMBERS_PAGE.label,
  ];

  const clientWorkspaceSectionNavItems = shouldFilterNonClientItems
    ? workspaceSectionNavItems.filter(
        x => !inAccessibleNavItemsForClient.includes(x.label),
      )
    : workspaceSectionNavItems;

  /**
   * This function calculate for each extension sidebar item
   * the external link url and the extension view location (in portal or new tab).
   *
   * @param extensionConfig
   * @param extId
   * @returns
   */
  const getExtensionLinkAccessibilityData = (
    extensionConfig: Constants.ExtensionConfig,
    extId: string,
  ) => {
    const defaultExtensionPath = `${Constants.EXTENSION_EMBED_PAGE.path}?id=${extId}`;
    let extensionItemAccessibilityData = {
      extensionLink: defaultExtensionPath,
      viewExtensionLocation: ExtensionItemViewLocation.IN_PORTAL,
    };
    if (extensionConfig.type === 'local') {
      // find all local connected extension channels that match this extensionID
      const connectedLocalExtensionItems = extensionItems.filter(
        extensionItem =>
          extensionItem.fields.extensionConfigId === extId &&
          extensionItem.fields.status === 'connected',
      );

      const connectedLocalExtensionItem =
        connectedLocalExtensionItems.length > 0
          ? connectedLocalExtensionItems[0]
          : null;

      if (connectedLocalExtensionItem) {
        const hasMoreThanOneConnectedEmbed =
          connectedLocalExtensionItems.length > 1;

        // default view local extensions in portal
        let viewExtensionLocation = ExtensionItemViewLocation.IN_PORTAL;
        // when client is viewing extension with embedLink and only has one channel then view in new tab
        if (
          isClient &&
          !hasMoreThanOneConnectedEmbed &&
          connectedLocalExtensionItem.fields.embedLink
        ) {
          viewExtensionLocation = ExtensionItemViewLocation.NEW_TAB;
        }
        extensionItemAccessibilityData = {
          ...extensionItemAccessibilityData,
          extensionLink:
            isClient &&
            connectedLocalExtensionItem.fields.embedLink &&
            !hasMoreThanOneConnectedEmbed
              ? connectedLocalExtensionItem.fields.embedLink
              : defaultExtensionPath,
          viewExtensionLocation,
        };
      }
    } else {
      // global extension
      extensionItemAccessibilityData = {
        ...extensionItemAccessibilityData,
        extensionLink:
          isClient && extensionConfig.embedLink
            ? extensionConfig.embedLink
            : defaultExtensionPath,
        viewExtensionLocation:
          isClient && extensionConfig.embedLink
            ? ExtensionItemViewLocation.NEW_TAB
            : ExtensionItemViewLocation.IN_PORTAL,
      };
    }

    return extensionItemAccessibilityData;
  };

  const extensionNavItems: NavItem[] = routeAccess.allowExtensionsPage
    ? Object.entries(extensionsSettings || {})
        .filter(([extensionID]) =>
          (moduleSettings || []).some(module => extensionID === module.id),
        )
        .filter(
          ([extensionID, extensionConfig]) =>
            isClient
              ? extensionConfig.type === 'global' ||
                setupExtensionConfigSet[extensionID]
              : true, // show all extensions for internal users
        )
        .sort(
          ([, extensionConfigA], [, extensionConfigB]) =>
            // first order by extension type so global is before local
            // then by `sort` which is number indicating position in the sidebar
            extensionConfigA.type.localeCompare(extensionConfigB.type) ||
            extensionConfigA.sort - extensionConfigB.sort,
        )
        .map(([extensionId, extensionConfig]) => {
          const {
            extensionLink: extensionItemPath,
            viewExtensionLocation,
          } = getExtensionLinkAccessibilityData(extensionConfig, extensionId);

          return {
            htmlId: `sidebar-extension-${extensionId}`,
            label: extensionConfig.name,
            isExternalLink:
              viewExtensionLocation === ExtensionItemViewLocation.NEW_TAB,
            path: extensionItemPath,
            icon: IconNameToComponent[extensionConfig.icon],
            canAccess: true,
            selected: queryIdParam === extensionId,
            disabled: planStatus.isBasicPlan,
            tooltipTitle: planStatus.isBasicPlan ? (
              <ExtensionTooltipTitle />
            ) : null,
            priority: (moduleSettings || []).findIndex(
              x => x.id === extensionId,
            ),
          };
        })
        .sort((a, b) => a.priority - b.priority)
    : [];

  const crmNavItem = {
    htmlId: 'sidebar-crm',
    label: 'Clients',
    path: Constants.CRM_PAGE.path,
    icon: Constants.CRM_PAGE.icon,
    canAccess: routeAccess.allowCRMPages,
    selected:
      pathname.includes(Constants.CRM_PAGE.path) ||
      pathname.includes(Constants.CLIENTS_IMPORT_PAGE.path),
  };

  const inboxNavItem = {
    htmlId: 'sidebar-inbox',
    label: Constants.INBOX_PAGE.label,
    path: Constants.INBOX_PAGE.path,
    icon: Constants.INBOX_PAGE.icon,
    children: useNotificationsBadge('inbox'),
    canAccess: routeAccess.allowInboxPage,
    selected: pathname.includes(Constants.INBOX_PAGE.path),
  };

  const extensionNavItem = {
    htmlId: 'extensions-item',
    label: EXTENSION_ITEM.label,
    path: extensionNavItems?.[0]?.path,
    icon: EXTENSION_ITEM.icon,
    canAccess: !isClient && extensionNavItems.length > 0, // Hide extensions main item if user is client or there are no extension items
    subItems: extensionNavItems,
    shouldCollapse: !!queryIdParam,
    priority: CoreModuleSortOrder.EXTENSIONS,
    disabled: planStatus.isBasicPlan || planStatus.isStarterPlan,
    tooltipTitle: planStatus.isBasicPlan ? <ExtensionTooltipTitle /> : null,
  };

  const homeNavItem = {
    htmlId: 'sidebar-home',
    label: Constants.HOME_PAGE.label,
    path: Constants.HOME_PAGE.path,
    icon: Constants.HOME_PAGE.icon,
    canAccess: routeAccess.allowHomePage,
    // home sidebar item is selected when user routes to home page
    // or trying to access a specific portal e.g. `/portal/${portalId}`
    selected:
      pathname === Constants.HOME_PAGE.path ||
      pathname.startsWith(Constants.PORTAL_PATH),
  };

  // const landingSectionNavItems: Array<NavItem> = [
  //   {
  //     htmlId: 'landing-page-section',
  //     label: Constants.LANDING_PAGE.label,
  //     path: Constants.LANDING_PAGE.path,
  //     icon: Constants.LANDING_PAGE.icon,
  //     canAccess: routeAccess.allowLandingPages,
  //     selected: pathname.includes(Constants.LANDING_PAGE.path),
  //   },
  // ];

  const defaultPortalSectionNavItems: NavItem[] = React.useMemo(
    () => [
      {
        htmlId: 'sidebar-messages',
        label: Constants.MESSAGES_PAGE.label,
        path: Constants.MESSAGES_PAGE.path,
        icon: Constants.MESSAGES_PAGE.icon,
        canAccess: routeAccess.allowMessagesPage,
        children: useNotificationsBadge('messaging'),
        priority: CoreModuleSortOrder.MESSAGES,
      },
      {
        htmlId: 'sidebar-files',
        label: Constants.FILES_PAGE.label,
        path: Constants.FILES_PAGE.path,
        icon: Constants.FILES_PAGE.icon,
        canAccess: routeAccess.allowFilesPage,
        children:
          // When files notification center flag is turned on
          // don't show files sidebar items notification count badge( internal user only )
          EnableFilesNotificationCenter && !isClient
            ? null
            : useNotificationsBadge('files'),
        priority: CoreModuleSortOrder.FILES,
        selected: pathname.includes(SIGNATURE_PAGE_V2.path),
      },

      {
        htmlId: 'sidebar-forms',
        label: Constants.FORMS_PAGE.label,
        path: Constants.FORMS_PAGE.path,
        icon: Constants.FORMS_PAGE.icon,
        canAccess: routeAccess.allowIntakeFormsPage,
        children: useNotificationsBadge('forms'),
        priority: CoreModuleSortOrder.FORMS,
      },
      {
        htmlId: 'sidebar-payments',
        label: 'Billing',
        path: Constants.INVOICES_PAGE.path,
        icon: Constants.INVOICES_PAGE.icon,
        priority: CoreModuleSortOrder.PAYMENTS,
        canAccess:
          routeAccess.allowPaymentsPage && routeAccess.allowSubscriptionPage,
        shouldCollapse:
          pathname === Constants.INVOICES_PAGE.path ||
          pathname === Constants.SUBSCRIPTION_PAGE.path,
        subItems: [
          {
            htmlId: 'sidebar-invoices',
            label: Constants.INVOICES_PAGE.label,
            path: Constants.INVOICES_PAGE.path,
            canAccess:
              routeAccess.allowPaymentsPage &&
              routeAccess.allowSubscriptionPage,
          },
          {
            htmlId: 'sidebar-subscriptions',
            label: Constants.SUBSCRIPTION_PAGE.label,
            path: Constants.SUBSCRIPTION_PAGE.path,
            canAccess:
              routeAccess.allowPaymentsPage &&
              routeAccess.allowSubscriptionPage,
          },
        ],
      },
      {
        htmlId: 'sidebar-payments', // payments for clients
        label: 'Billing',
        path: Constants.INVOICES_PAGE.path,
        icon: Constants.INVOICES_PAGE.icon,
        canAccess:
          routeAccess.allowPaymentsPage && !routeAccess.allowSubscriptionPage,
        children: useNotificationsBadge('billing'),
        priority: CoreModuleSortOrder.PAYMENTS,
      },
      {
        htmlId: 'sidebar-knowledgeBase',
        label: Constants.KNOWLEDGE_BASE_PAGE.label,
        path: Constants.KNOWLEDGE_BASE_PAGE.path,
        icon: Constants.KNOWLEDGE_BASE_PAGE.icon,
        canAccess: routeAccess.allowKnowledgeBasePage,
        priority: CoreModuleSortOrder.KNOWLEDGE_BASE,
        selected: pathname.includes(Constants.KNOWLEDGE_BASE_PAGE.path),
      },
    ],
    [routeAccess],
  );

  const getPortalNavItems = () => [
    {
      htmlId: 'sidebar-modules-settings',
      label: MODULE_MANAGEMENT_PAGE.label,
      path: '/module-management', // paths to be changed from settings to main
      canAccess: routeAccess.allowSettingsModuleManagement,
      icon: MODULE_MANAGEMENT_PAGE.icon,
    },
    {
      htmlId: 'sidebar-portal-customization',
      label: CUSTOMIZATION_PAGE.label,
      path: '/customization',
      icon: PORTAL_ITEM.icon,
      canAccess: routeAccess.allowSettingsCustomizationPage,
    },
  ];

  /**
   * This function determines portal section nav items
   * from modules settings.
   * @returns
   */
  const getCoreModulesFromModuleSettings = (): NavItem[] => {
    // when module settings is not defined return the default portal sections
    // navigation items.
    const modules = moduleSettings || [];
    // New portals do not have module settings or extensions.
    if (!modules.length && !extensionsSettings) {
      return defaultPortalSectionNavItems.filter(item => item?.canAccess);
    }

    // When a new extension is created, it is added to the extensionsSettings and thus to be taken account of in sidebar

    let allNavItems = defaultPortalSectionNavItems;
    if (isClient) {
      allNavItems = allNavItems.concat(extensionNavItems); // Only concat for client. For admin, display as subItems
    }

    const hasOnlyExtensionsModules = modules.every(
      ms => ms.type === 'local' || ms.type === 'global',
    );
    // if moduleSettings has only extensions append extensions
    // below the default modules.
    if (hasOnlyExtensionsModules) {
      const moduleItems = allNavItems
        .filter(item => item?.canAccess)
        .filter(navItem => {
          const itemId = navItem.htmlId.replace(extensionNavItemIdPrefix, '');
          const moduleSetting = modules.find(setting => setting.id === itemId);
          // filter name items found in module settings and disabled
          if (moduleSetting && moduleSetting.disabled) return false;

          // if itemId is not found in module settings, then it should be included in sidebar
          return true;
        });

      return isClient ? moduleItems : [...moduleItems, extensionNavItem];
    }

    const clientNavItems = modules
      .map(module => {
        if (module.disabled) return null;
        const moduleNavItem = allNavItems.find(item => {
          const itemId = item.htmlId.includes(extensionNavItemIdPrefix)
            ? item.htmlId.replace(extensionNavItemIdPrefix, '')
            : item.htmlId.replace(moduleNavItemIdPrefix, '');
          return itemId === module.id && item.canAccess;
        });

        if (moduleNavItem) {
          const navItemIconName =
            module.icon ||
            getModuleIcon(module.type, module.id, extensionsSettings);
          const navItemLabel =
            module.label ||
            getModuleName(module.type, module.id, extensionsSettings);

          return {
            ...moduleNavItem,
            icon: IconNameToComponent[navItemIconName as IconType],
            label: navItemLabel,
          };
        }
        return null;
      })
      .filter(module => module);

    // include crm and marketing site nav items, they will not be shown
    // for users without permissions based on `canAccess` property.
    const items = [...clientNavItems, extensionNavItem];

    if (!isClient) {
      // For internal admin users, core modules would always remain in same sort order.
      items.sort((a, b) => a.priority - b.priority);
    }
    return items as NavItem[];
  };

  const homeNavItems = React.useMemo(
    () => [
      homeNavItem,
      crmNavItem,
      ...(EnableFilesNotificationCenter ? [inboxNavItem] : []),
    ],
    [EnableFilesNotificationCenter, routeAccess],
  );

  const coreModulesNavItems: Array<NavItem> = React.useMemo(
    () => getCoreModulesFromModuleSettings(),
    [
      moduleSettings,
      extensionNavItem,
      isClient,
      extensionNavItems,
      extensionsSettings,
      planStatus,
    ],
  );

  const portalNavItems = React.useMemo(() => getPortalNavItems(), [
    routeAccess,
    isClient,
  ]);

  const mainSidebarSections: Array<NavSection> = [
    {
      htmlId: 'section-core',
      title: '',
      navItems: homeNavItems,
      canAccess: homeNavItems.some(item => item.canAccess),
    },
    {
      htmlId: 'section-modules',
      title: 'Apps',
      navItems: coreModulesNavItems,
      canAccess: coreModulesNavItems.some(item => item.canAccess),
    },
    {
      htmlId: 'section-portal',
      title: 'Preferences',
      navItems: portalNavItems,
      canAccess: portalNavItems.some(x => x.canAccess),
    },
  ];

  const moduleSettingsSectionNavItems: Array<NavItem> = React.useMemo(
    () => [
      {
        htmlId: 'messaging-setting-page',
        label: MESSAGING_PAGE.label,
        path: MESSAGING_PAGE.path,
        icon: MESSAGING_PAGE.icon,
        canAccess:
          routeAccess.allowSettingsModuleMessaging &&
          Boolean(
            coreModulesNavItems.find(
              item => item.path === Constants.MESSAGES_PAGE.path,
            ),
          ),
      },
      {
        htmlId: 'file-setting-page',
        label: FILES_SETTINGS_PAGE.label,
        path: FILES_SETTINGS_PAGE.path,
        icon: FILES_SETTINGS_PAGE.icon,
        canAccess:
          routeAccess.allowSettingsFiles &&
          Boolean(
            coreModulesNavItems.find(item => item.path === FILES_PAGE.path),
          ),
      },
      {
        htmlId: 'payments-setting-page',
        label: PAYMENTS_PAGE.label,
        path: PAYMENTS_PAGE.path,
        icon: PAYMENTS_PAGE.icon,
        canAccess:
          routeAccess.allowSettingsModulePayments &&
          Boolean(
            coreModulesNavItems.find(
              item => item.path === Constants.INVOICES_PAGE.path,
            ),
          ),
      },
    ],
    [routeAccess, coreModulesNavItems],
  );

  const settingsSidebarSections: Array<NavSection> = [
    {
      htmlId: 'section-about-settings',
      title: 'Me',
      navItems: meSectionNavItems,
      canAccess: meSectionNavItems.some(item => item.canAccess),
    },
    {
      htmlId: 'section-workspace-settings',
      title: 'Workspace',
      navItems: clientWorkspaceSectionNavItems,
      canAccess: clientWorkspaceSectionNavItems.some(item => item.canAccess),
    },

    {
      htmlId: 'section-module-settings',
      title: 'App settings',
      navItems: moduleSettingsSectionNavItems,
      canAccess: moduleSettingsSectionNavItems.some(item => item.canAccess),
    },
  ];

  return sidebarType === 'settings'
    ? settingsSidebarSections
    : mainSidebarSections;
};
